import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, MessageCircle, Facebook, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { NewsletterBox } from "@/components/NewsletterBox";

const SUPPORT_SLUG = "stress-da-debiti-come-riconoscerlo-e-dove-chiedere-aiuto";

const SupportForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Inserisci nome e telefono"); return; }
    setSending(true);
    try {
      await api.post("/support-requests", form);
      setDone(true);
    } catch {
      toast.error("Errore nell'invio. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  if (done) return (
    <div className="mt-12 border border-[#E5E7EB] bg-[#F8FAFC] rounded-lg p-8 text-center" data-testid="support-success">
      <HeartHandshake size={28} className="mx-auto text-[#2d6fc4]" strokeWidth={1.5}/>
      <h3 className="font-serif text-2xl text-[#2d6fc4] mt-3">Richiesta ricevuta</h3>
      <p className="text-[#333333] text-sm mt-2">Grazie per la fiducia. Ti ricontatteremo noi, in modo riservato, per fissare il colloquio telefonico gratuito.</p>
    </div>
  );

  return (
    <div className="mt-12 border border-[#E5E7EB] bg-[#F8FAFC] rounded-lg p-8" data-testid="support-form">
      <h3 className="font-serif text-2xl md:text-3xl text-[#2d6fc4] flex items-center gap-2"><HeartHandshake size={24} strokeWidth={1.5}/> Richiedi il colloquio telefonico gratuito</h3>
      <p className="text-sm text-[#333333] mt-3">Compila il modulo: ti ricontatteremo noi, con la massima riservatezza, per un colloquio gratuito di supporto.</p>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <input value={form.name} onChange={set("name")} required placeholder="Nome *" data-testid="support-name"
          className="border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm bg-white text-[#333333] focus:outline-none focus:border-[#2d6fc4]"/>
        <input value={form.phone} onChange={set("phone")} required placeholder="Telefono *" data-testid="support-phone"
          className="border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm bg-white text-[#333333] focus:outline-none focus:border-[#2d6fc4]"/>
        <input value={form.email} onChange={set("email")} type="email" placeholder="Email (facoltativa)" data-testid="support-email"
          className="border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm bg-white text-[#333333] focus:outline-none focus:border-[#2d6fc4] md:col-span-2"/>
        <textarea value={form.message} onChange={set("message")} rows={3} placeholder="Se vuoi, raccontaci in due righe la tua situazione (facoltativo)" data-testid="support-message"
          className="border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm bg-white text-[#333333] focus:outline-none focus:border-[#2d6fc4] md:col-span-2"/>
        <button type="submit" disabled={sending} data-testid="support-submit"
          className="btn-cta px-7 py-3.5 rounded-lg text-sm tracking-wide md:col-span-2 disabled:opacity-60">
          {sending ? "Invio in corso…" : "Invia la richiesta riservata"}
        </button>
      </form>
      <p className="text-xs text-[#94A3B8] mt-3">I tuoi dati sono trattati in modo riservato e usati solo per ricontattarti.</p>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null); setError(false); setRelated([]);
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    api.get("/blog").then(r => {
      const others = r.data.filter(p => p.slug !== post.slug);
      const same = others.filter(p => p.category === post.category);
      const rest = others.filter(p => p.category !== post.category);
      setRelated([...same, ...rest].slice(0, 2));
    }).catch(() => {});
  }, [post]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Blog Sdebitiamoci Srl`;
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", post.excerpt);
    }
  }, [post]);

  if (error) return (
    <div className="pt-40 pb-24 container-editorial" data-testid="blog-post-notfound">
      <p className="text-[#333333]">Articolo non trovato.</p>
      <Link to="/blog" className="text-[#2d6fc4] link-underline">← Torna al blog</Link>
    </div>
  );
  if (!post) return <div className="pt-40 pb-24 container-editorial text-[#333333]">Caricamento…</div>;

  const blocks = post.content.split(/\n\s*\n/).filter(Boolean);

  return (
    <div className="pt-32 pb-24" data-testid="blog-post-page">
      <article className="container-editorial max-w-3xl">
        <Link to="/blog" className="text-sm text-[#2d6fc4] inline-flex items-center gap-1 link-underline pb-0.5" data-testid="blog-back-link">
          <ArrowLeft size={14}/> Tutti gli articoli
        </Link>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4] mt-8">
          {post.category} · {new Date(post.published_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mt-4 text-[#2d6fc4]">{post.title}</h1>
        <p className="mt-3 text-xs text-[#333333] flex items-center gap-1"><Clock size={12}/> {post.reading_minutes} min di lettura</p>
        {post.image && <img src={post.image} alt={post.title} className="w-full aspect-[2/1] object-cover rounded-lg mt-8"/>}
        <div className="mt-10 space-y-6">
          {blocks.map((b) => b.startsWith("## ")
            ? <h2 key={b} className="font-serif text-2xl md:text-3xl text-[#2d6fc4] pt-4">{b.slice(3)}</h2>
            : <p key={b} className="text-[#333333] leading-relaxed">{b}</p>
          )}
        </div>
        {post.slug === SUPPORT_SLUG && <SupportForm />}
        <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="blog-share-section">
          <span className="text-sm text-[#333333]">Condividi l'articolo:</span>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${post.title} — ${window.location.href}`)}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="blog-share-whatsapp"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-white bg-[#25D366] hover:bg-[#1ebe5b] transition-colors"
          >
            <MessageCircle size={15}/> WhatsApp
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="blog-share-facebook"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-white bg-[#1877F2] hover:bg-[#1464d0] transition-colors"
          >
            <Facebook size={15}/> Facebook
          </a>
        </div>
        <div className="mt-10 border-t border-[#E5E7EB] pt-8">
          <p className="text-[#333333] text-sm">Hai una situazione simile? La prima consulenza è gratuita e senza impegno.</p>
          <Link to="/prenota" data-testid="blog-cta-booking" className="btn-cta inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm tracking-wide mt-4">
            Prenota una chiamata gratuita <ArrowUpRight size={15}/>
          </Link>
        </div>
        {related.length > 0 && (
          <div className="mt-14 border-t border-[#E5E7EB] pt-10" data-testid="blog-related-section">
            <h2 className="font-serif text-2xl md:text-3xl text-[#2d6fc4]">Potrebbe interessarti anche</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {related.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-related-${p.slug}`}
                  className="block border border-[#E5E7EB] bg-white rounded-lg overflow-hidden hover:border-[#2d6fc4]"
                  style={{ transition: "border-color 200ms ease" }}>
                  {p.image && <img src={p.image} alt={p.title} loading="lazy" className="w-full aspect-[3/2] object-cover"/>}
                  <div className="p-6">
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">{p.category}</p>
                    <h3 className="font-serif text-xl mt-2 text-[#2d6fc4] leading-tight">{p.title}</h3>
                    <p className="mt-3 text-xs text-[#333333] flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock size={11}/> {p.reading_minutes} min</span>
                      <span className="text-[#2d6fc4] inline-flex items-center gap-1">Leggi <ArrowUpRight size={11}/></span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        <NewsletterBox />
      </article>
    </div>
  );
}
